import React, { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import './NetworkMetricsDashboard.css';

const NetworkMetricsDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchNetworkData();
  }, []);

  const fetchNetworkData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosPrivate.get('/NetworkAnalysis/analyzeNetwork');

      const transformedData = {
        networkStructure: response.data.networkMetrics,
        centralityRankings: {
          topByDegree: response.data.topAcquirers?.map(a => ({
            company: a.company,
            centrality: a.degreeCentrality,
            interpretation: a.interpretation
          })) || [],
          topByBetweenness: response.data.bridgeCompanies?.map(b => ({
            company: b.company,
            centrality: b.betweennessCentrality,
            interpretation: b.interpretation
          })) || [],
          topByCloseness: response.data.centralBrokers?.map(c => ({
            company: c.company,
            centrality: c.closenessCentrality,
            interpretation: c.interpretation
          })) || [],
          topByEigenvector: []
        },
        communities: {
          totalCommunities: response.data.communities?.length || 0,
          largestCommunitySize: Math.max(...(response.data.communities?.map(c => c.size) || [0])),
          communityList: response.data.communities?.map(c => ({
            communityId: c.communityId,
            size: c.size,
            topMembers: c.members?.slice(0, 5) || []
          })) || []
        },
        academicSummary: response.data.academicSummary,
        strategicInsights: response.data.strategicInsights
      };

      setData(transformedData);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load network analysis');
      console.error('Network analysis error:', err);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="network-metrics-container">
        <div className="loading-spinner">Analyzing network structure...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="network-metrics-container">
        <div className="error-message">
          <h3>Error Loading Network Analysis</h3>
          <p>{error}</p>
          <button onClick={fetchNetworkData}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="network-metrics-container">
      <header className="network-header">
        <h1>🕸️ Network Analysis Dashboard</h1>
        <p className="subtitle">
          Graph-Based Analysis of M&A Relationship Networks
        </p>
      </header>

      {}
      <section className="dataset-info-section">
        <h2>Network Overview</h2>
        <div className="info-grid">
          <div className="info-card">
            <div className="info-label">Total Nodes</div>
            <div className="info-value">{data.networkStructure?.totalNodes || 'N/A'}</div>
            <div className="info-sublabel">Companies</div>
          </div>
          <div className="info-card">
            <div className="info-label">Total Edges</div>
            <div className="info-value">{data.networkStructure?.totalEdges || 'N/A'}</div>
            <div className="info-sublabel">Acquisitions</div>
          </div>
          <div className="info-card">
            <div className="info-label">Network Density</div>
            <div className="info-value">{data.networkStructure?.density != null ? data.networkStructure.density.toFixed(4) : 'N/A'}</div>
            <div className="info-sublabel">
              {data.networkStructure?.density != null ? (
                data.networkStructure.density < 0.01 ? 'Sparse' :
                data.networkStructure.density < 0.05 ? 'Moderate' : 'Dense'
              ) : ''}
            </div>
          </div>
          <div className="info-card">
            <div className="info-label">Network Diameter</div>
            <div className="info-value">{data.networkStructure?.diameter || 'N/A'}</div>
            <div className="info-sublabel">Max path length</div>
          </div>
        </div>
      </section>

      {}
      <section className="structure-metrics-section">
        <h2>Network Structure Metrics</h2>
        <div className="structure-grid">
          <div className="structure-card">
            <h3>Average Path Length</h3>
            <div className="metric-value">{data.networkStructure?.averagePathLength != null ? data.networkStructure.averagePathLength.toFixed(2) : 'N/A'}</div>
            <p className="metric-interpretation">
              {data.networkStructure?.averagePathLength != null && data.networkStructure.averagePathLength < 6
                ? '✓ Small-world property detected'
                : 'Dispersed network structure'}
            </p>
          </div>
          <div className="structure-card">
            <h3>Clustering Coefficient</h3>
            <div className="metric-value">{data.networkStructure?.clusteringCoefficient != null ? data.networkStructure.clusteringCoefficient.toFixed(3) : 'N/A'}</div>
            <p className="metric-interpretation">
              {data.networkStructure?.clusteringCoefficient != null ? (
                data.networkStructure.clusteringCoefficient < 0.3
                  ? 'Low clustering tendency'
                  : data.networkStructure.clusteringCoefficient < 0.6
                  ? 'Moderate clustering tendency'
                  : 'Strong community structure'
              ) : 'Pending'}
            </p>
          </div>
        </div>
        {data.networkStructure?.interpretation && (
          <div className="interpretation-box">
            <strong>Interpretation:</strong> {data.networkStructure.interpretation}
          </div>
        )}
      </section>

      {}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'degree' ? 'active' : ''}`}
          onClick={() => setActiveTab('degree')}
        >
          Degree Centrality
        </button>
        <button
          className={`tab-button ${activeTab === 'betweenness' ? 'active' : ''}`}
          onClick={() => setActiveTab('betweenness')}
        >
          Betweenness
        </button>
        <button
          className={`tab-button ${activeTab === 'closeness' ? 'active' : ''}`}
          onClick={() => setActiveTab('closeness')}
        >
          Closeness
        </button>
        <button
          className={`tab-button ${activeTab === 'eigenvector' ? 'active' : ''}`}
          onClick={() => setActiveTab('eigenvector')}
        >
          Eigenvector
        </button>
        <button
          className={`tab-button ${activeTab === 'communities' ? 'active' : ''}`}
          onClick={() => setActiveTab('communities')}
        >
          Communities
        </button>
      </div>

      {}
      {activeTab === 'overview' && data.centralityRankings && (
        <section className="overview-section">
          <h2>Top Players by Centrality Type</h2>
          <div className="overview-grid">
            <div className="centrality-preview">
              <h3>🎯 Serial Acquirers (Degree)</h3>
              <ol>
                {data.centralityRankings.topByDegree?.slice(0, 5).map((company, idx) => (
                  <li key={idx}>
                    <span className="company-name">{company.company}</span>
                    <span className="centrality-score">{company.centrality.toFixed(4)}</span>
                  </li>
                )) || <li>No data available</li>}
              </ol>
            </div>
            <div className="centrality-preview">
              <h3>🌉 Bridge Companies (Betweenness)</h3>
              <ol>
                {data.centralityRankings.topByBetweenness?.slice(0, 5).map((company, idx) => (
                  <li key={idx}>
                    <span className="company-name">{company.company}</span>
                    <span className="centrality-score">{company.centrality.toFixed(4)}</span>
                  </li>
                )) || <li>No data available</li>}
              </ol>
            </div>
            <div className="centrality-preview">
              <h3>🎪 Central Brokers (Closeness)</h3>
              <ol>
                {data.centralityRankings.topByCloseness?.slice(0, 5).map((company, idx) => (
                  <li key={idx}>
                    <span className="company-name">{company.company}</span>
                    <span className="centrality-score">{company.centrality.toFixed(4)}</span>
                  </li>
                )) || <li>No data available</li>}
              </ol>
            </div>
            <div className="centrality-preview">
              <h3>👑 Elite Network (Eigenvector)</h3>
              <ol>
                {data.centralityRankings.topByEigenvector?.slice(0, 5).map((company, idx) => (
                  <li key={idx}>
                    <span className="company-name">{company.company}</span>
                    <span className="centrality-score">{company.centrality.toFixed(4)}</span>
                  </li>
                )) || <li>No data available</li>}
              </ol>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'degree' && data.centralityRankings?.topByDegree && (
        <section className="centrality-section">
          <h2>Degree Centrality (Serial Acquirers)</h2>
          <p className="centrality-description">
            <strong>Definition:</strong> Number of direct connections (acquisitions made).
            Identifies companies with the most aggressive acquisition strategies.
          </p>
          <table className="centrality-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Company</th>
                <th>Centrality Score</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {data.centralityRankings.topByDegree.map((company, idx) => (
                <tr key={idx}>
                  <td className="rank-cell">{idx + 1}</td>
                  <td className="company-cell">{company.company}</td>
                  <td className="score-cell">{company.centrality.toFixed(4)}</td>
                  <td className="interpretation-cell">{company.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {activeTab === 'betweenness' && data.centralityRankings?.topByBetweenness && (
        <section className="centrality-section">
          <h2>Betweenness Centrality (Bridge Companies)</h2>
          <p className="centrality-description">
            <strong>Definition:</strong> Frequency on shortest paths between other nodes.
            Identifies companies that connect different communities in the network.
          </p>
          <table className="centrality-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Company</th>
                <th>Centrality Score</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {data.centralityRankings.topByBetweenness.map((company, idx) => (
                <tr key={idx}>
                  <td className="rank-cell">{idx + 1}</td>
                  <td className="company-cell">{company.company}</td>
                  <td className="score-cell">{company.centrality.toFixed(4)}</td>
                  <td className="interpretation-cell">{company.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {activeTab === 'closeness' && data.centralityRankings?.topByCloseness && (
        <section className="centrality-section">
          <h2>Closeness Centrality (Central Brokers)</h2>
          <p className="centrality-description">
            <strong>Definition:</strong> Inverse of average distance to all other nodes.
            Identifies companies with efficient access to the entire network.
          </p>
          <table className="centrality-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Company</th>
                <th>Centrality Score</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {data.centralityRankings.topByCloseness.map((company, idx) => (
                <tr key={idx}>
                  <td className="rank-cell">{idx + 1}</td>
                  <td className="company-cell">{company.company}</td>
                  <td className="score-cell">{company.centrality.toFixed(4)}</td>
                  <td className="interpretation-cell">{company.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {activeTab === 'eigenvector' && data.centralityRankings?.topByEigenvector && (
        <section className="centrality-section">
          <h2>Eigenvector Centrality (Elite Network Members)</h2>
          <p className="centrality-description">
            <strong>Definition:</strong> Quality of connections (connected to important nodes).
            Identifies companies in the elite consolidator network.
          </p>
          <table className="centrality-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Company</th>
                <th>Centrality Score</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {data.centralityRankings.topByEigenvector.map((company, idx) => (
                <tr key={idx}>
                  <td className="rank-cell">{idx + 1}</td>
                  <td className="company-cell">{company.company}</td>
                  <td className="score-cell">{company.centrality.toFixed(4)}</td>
                  <td className="interpretation-cell">{company.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {activeTab === 'communities' && data.communities && (
        <section className="communities-section">
          <h2>Community Structure Detection</h2>
          <p className="section-description">
            Connected components analysis reveals distinct consolidation communities in the EDA industry.
          </p>
          <div className="communities-summary">
            <div className="community-stat">
              <div className="stat-label">Total Communities</div>
              <div className="stat-value">{data.communities.totalCommunities || 'N/A'}</div>
            </div>
            <div className="community-stat">
              <div className="stat-label">Largest Community</div>
              <div className="stat-value">{data.communities.largestCommunitySize ? `${data.communities.largestCommunitySize} companies` : 'N/A'}</div>
            </div>
          </div>
          <div className="communities-list">
            {data.communities.communityList?.map((community) => (
              <div key={community.communityId} className="community-card">
                <div className="community-header">
                  <h3>Community {community.communityId}</h3>
                  <span className="community-size">{community.size} members</span>
                </div>
                <div className="community-members">
                  <strong>Top Members:</strong>
                  <ul>
                    {community.topMembers.map((member, idx) => (
                      <li key={idx}>{member}</li>
                    ))}
                  </ul>
                  {community.size > 5 && (
                    <p className="more-members">... and {community.size - 5} more</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {}
      <section className="academic-summary-section">
        <h2>Academic Summary</h2>
        <div className="summary-box">
          <pre>{data.academicSummary}</pre>
        </div>
      </section>

      {}
      <div className="actions-section">
        <button className="refresh-button" onClick={fetchNetworkData}>
          🔄 Refresh Analysis
        </button>
      </div>
    </div>
  );
};

export default NetworkMetricsDashboard;
